/**
 * Mission 10's executioners (03 §2; 01 §3: "once open, heavy brown-uniformed troops come to
 * kill the hostages"). Only once the prison door is open: while it is shut the threat is the
 * 40 s demolition charge (03 §2, mission 10), and killing the officer must be able to stop it. Then up
 * to two men - the rocket squad first, guards near the prison if the alarm is up - are given the job: they make for the nearest hostage - in his cell or out of it - and,
 * standing over him, kill one every few seconds. They are soldiers still: one who has the
 * ranger in plain sight fights him instead, which is how hostages are defended. Kill one and
 * the next man near the prison takes his place.
 *
 * Two halves: `stepExecutionerRoster` (from `stepObjectives`) keeps `objective.executioners`;
 * `stepExecutioner` (from the garrison's tick, before the state machine) does the walking and the killing.
 */

import { makeNoise } from '../alert';
import { type Ctx, isFighter } from '../ctx';
import { travel, turnToward } from '../enemies/movement';
import { isAutomated } from '../enemies/specials/kinds';
import { atan2, cos, sin } from '../math';
import { spawnBullet } from '../projectiles';
import { ENEMY, EXECUTION, NOISE, OBJECTIVES } from '../tuning';
import type { Captive, Enemy, World } from '../types';
import { dist, loseCaptive, nearestProp, propDestroyed, propDistance } from './common';

const ROSTER_EVERY_TICKS = 30;

/**
 * Hostages who can still be killed: in the cell, or out in the open behind the ranger. Once the guards have been
 * told to shoot the captive rather than lose him (`shootTheCaptive`, 10 §3), that is everyone he is bringing out.
 */
function victims(w: World): Captive[] {
  const o = w.objective;
  return o.captives.filter((c) => (c.kind === 'hostage' || o.shootOrdered) && (c.state === 'held' || c.state === 'following'));
}

function nearestVictim(list: Captive[], x: number, z: number): { captive: Captive; distance: number } | null {
  let best: { captive: Captive; distance: number } | null = null;
  for (const captive of list) {
    const distance = dist(captive.x, captive.z, x, z);
    if (!best || distance < best.distance) best = { captive, distance };
  }
  return best;
}

export function stepExecutionerRoster(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  // Data, not the mission's name: there are hostages to kill, or the order to shoot the captive has gone out.
  if (!o.shootOrdered && !statics.def.captives.some((c) => c.kind === 'hostage')) return;
  const prey = victims(w);
  if (prey.length === 0) {
    o.executioners = [];
    return;
  }
  // A man who is dead, down for good or gone has dropped the job.
  o.executioners = o.executioners.filter((x) => w.enemies.some((e) => e.id === x.enemyId && isFighter(e)));
  const doorOpen = o.woken.objectiveOpened || o.shootOrdered;
  if (!doorOpen || o.executioners.length >= EXECUTION.max) return;
  if (w.tick % ROSTER_EVERY_TICKS !== 0) return;

  const prison = statics.def.props.find((p) => p.kind === 'prison');
  const pool: Array<{ e: Enemy; squad: boolean; distance: number }> = [];
  for (const e of w.enemies) {
    if (!isFighter(e) || isAutomated(e.kind) || e.kind === 'officer' || e.mans !== null) continue;
    if (o.executioners.some((x) => x.enemyId === e.id)) continue;
    // The rocket squad that comes in when the door goes is sent for exactly this, from wherever it stands.
    const squad = e.kind === 'rocketTrooper' && doorOpen;
    const distance = nearestVictim(prey, e.x, e.z)?.distance ?? Infinity;
    const toPrison = prison ? dist(prison.x, prison.z, e.x, e.z) : Infinity;
    if (!squad && (w.alert < 3 || Math.min(distance, toPrison) > EXECUTION.recruitRadius)) continue;
    pool.push({ e, squad, distance });
  }
  pool.sort((a, b) => Number(b.squad) - Number(a.squad) || a.distance - b.distance || a.e.id - b.e.id);
  for (const pick of pool) {
    if (o.executioners.length >= EXECUTION.max) break;
    o.executioners.push({ enemyId: pick.e.id, ticks: 0 });
    pick.e.path = [];
  }
}

/** Close enough to do it: beside a hostage in the open, or at the wall of the cell that holds him. */
function inReach(ctx: Ctx, e: Enemy, c: Captive, distance: number): boolean {
  if (distance <= EXECUTION.reach) return true;
  if (c.state !== 'held') return false;
  const cell = nearestProp(ctx.statics.def, ['prison'], c.x, c.z);
  if (!cell || cell.distance > OBJECTIVES.cellRadius || propDestroyed(ctx.world, cell.prop.id)) return false;
  return propDistance(cell.prop, e.x, e.z) <= EXECUTION.cellReach;
}

/** Before the state machine, for a living soldier who is up. True = he spent this tick on the job. */
export function stepExecutioner(ctx: Ctx, e: Enemy): boolean {
  const w = ctx.world;
  const entry = w.objective.executioners.find((x) => x.enemyId === e.id);
  if (!entry) return false;
  // The ranger in plain sight comes first - and a man who has broken, or is off on an errand, is no executioner.
  const fighting = e.state === 'combat' && e.seesRanger;
  const busy = fighting || e.state === 'flee' || e.state === 'runner' || e.state === 'rescue';
  const target = busy ? null : nearestVictim(victims(w), e.x, e.z);
  if (!target) {
    entry.ticks = 0;
    return false;
  }
  const c = target.captive;
  // Whatever he was in the middle of - a peek from cover, a rocket on his shoulder - he has dropped it.
  e.peeking = false;
  if (!inReach(ctx, e, c, target.distance)) {
    entry.ticks = 0;
    travel(ctx, e, c.x, c.z, ENEMY.huntSpeed, EXECUTION.reach * 0.8);
    return true;
  }
  e.moving = false;
  const bearing = atan2(c.x - e.x, c.z - e.z);
  turnToward(e, bearing, 9);
  entry.ticks++;
  if (entry.ticks < EXECUTION.everyTicks) return true;
  entry.ticks = 0;
  // The round is for show - enemy rounds only ever strike the ranger (projectiles.ts) - and the deed is settled here.
  const dirX = sin(bearing);
  const dirZ = cos(bearing);
  const reach = Math.max(0.5, target.distance - 0.6);
  spawnBullet(ctx, false, e.x + dirX * 0.6, e.z + dirZ * 0.6, dirX, dirZ, reach, -1, false);
  e.sinceShot = 0;
  makeNoise(ctx, e.x, e.z, NOISE.enemyShot, 'gunfire', e.id);
  loseCaptive(ctx, c);
  return true;
}
